import { Mail, ShieldOff } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "@/components/common";
import {
  useResetTrustedDevices,
  useRoles,
  useSendPasswordResetLink,
  useUpdateUser,
} from "@/hooks/useAdmin";
import type { ManagedUser } from "@/types/rbac";

interface EditUserModalProps {
  user: ManagedUser | null;
  onClose: () => void;
}

export function EditUserModal({ user, onClose }: EditUserModalProps) {
  const { data: roles = [] } = useRoles();
  const updateUser = useUpdateUser();
  const sendResetLink = useSendPasswordResetLink();
  const resetDevices = useResetTrustedDevices();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [rauNumber, setRauNumber] = useState("");
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setRoleId(user.role.id);
      setRauNumber(user.rauNumber ?? "");
      setResetSent(false);
      sendResetLink.reset();
      resetDevices.reset();
    }
  }, [user, sendResetLink.reset, resetDevices.reset]);

  function handleClose() {
    updateUser.reset();
    sendResetLink.reset();
    resetDevices.reset();
    setResetSent(false);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      return;
    }
    updateUser.mutate(
      {
        userId: user.id,
        payload: { firstName, lastName, roleId, rauNumber: rauNumber.trim() },
      },
      { onSuccess: handleClose },
    );
  }

  function handleSendResetLink() {
    if (!user) {
      return;
    }
    sendResetLink.mutate(user.id, {
      onSuccess: () => setResetSent(true),
    });
  }

  const roleOptions = roles.map((r) => ({
    value: r.id,
    label: r.name,
  }));

  return (
    <Modal isOpen={user !== null} onClose={handleClose} title="Edytuj agenta">
      <form onSubmit={handleSubmit} className="space-y-4">
        {updateUser.isError && (
          <Alert variant="error">
            {(updateUser.error as { message?: string })?.message ??
              "Nie udało się zapisać zmian"}
          </Alert>
        )}

        {user && (
          <div className="rounded-lg bg-subtle px-3 py-2">
            <p className="break-all text-sm text-dim">{user.email}</p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Imię"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
            required
          />
          <Input
            label="Nazwisko"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            required
          />
        </div>

        <Select
          label="Rola"
          options={roleOptions}
          value={roleId}
          onChange={setRoleId}
        />

        <Input
          label="Numer RAU (rejestr pośredników KNF)"
          value={rauNumber}
          onChange={(e) => {
            setRauNumber(e.target.value);
          }}
          placeholder="np. 11260776/A/0001"
          maxLength={30}
          hint="Pokazywany klientom w klauzuli RODO jako numer agenta."
        />

        {/* Password reset link section */}
        <div className="rounded-lg border border-edge p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-sub">Reset hasła</p>
              <p className="text-xs text-dim">
                Wyślij link do zmiany hasła na e-mail agenta
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSendResetLink}
              isLoading={sendResetLink.isPending}
              disabled={resetSent}
              leftIcon={<Mail size={14} />}
            >
              {resetSent ? "Wysłano" : "Wyślij link"}
            </Button>
          </div>
          {resetSent && (
            <p className="mt-2 break-words text-xs text-green-600">
              Link do resetowania hasła został wysłany na adres {user?.email}.
            </p>
          )}
          {sendResetLink.isError && (
            <p className="mt-2 text-xs text-red-600">
              {(sendResetLink.error as { message?: string })?.message ??
                "Nie udało się wysłać linku"}
            </p>
          )}
        </div>

        {/* Trusted devices reset */}
        <div className="rounded-lg border border-edge p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-sub">Zaufane urządzenia</p>
              <p className="text-xs text-dim">
                Wymuś kod z e-maila przy następnym logowaniu na każdym
                urządzeniu (np. po zgubieniu laptopa)
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (user) {
                  resetDevices.mutate(user.id);
                }
              }}
              isLoading={resetDevices.isPending}
              disabled={resetDevices.isSuccess}
              leftIcon={<ShieldOff size={14} />}
            >
              {resetDevices.isSuccess ? "Zresetowano" : "Resetuj"}
            </Button>
          </div>
          {resetDevices.isSuccess && (
            <p className="mt-2 text-xs text-green-600">
              Usunięto zaufane urządzenia: {resetDevices.data.removed}.
            </p>
          )}
          {resetDevices.isError && (
            <p className="mt-2 text-xs text-red-600">
              {resetDevices.error.message ||
                "Nie udało się zresetować urządzeń"}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            isLoading={updateUser.isPending}
            disabled={!firstName || !lastName || !roleId}
          >
            Zapisz zmiany
          </Button>
        </div>
      </form>
    </Modal>
  );
}
